import NumberHelper from '@/core/helpers/NumberHelper';
import StringHelper from '@/core/helpers/StringHelper';
import { ArrayHelper } from '@/core/helpers/ArrayHelper';
import PriceFacade from '@/core/features/price/facades/PriceFacade';
import { sortBySize } from '@/app/features/product-view/constants/ProductViewConstants';
import LivestreamProductEntity from '../entities/LivestreamProductEntity';
import LivestreamProductVariationEntity from '../entities/LivestreamProductVariationEntity';
import LivestreamProductMapperInterface from '../interface-adapters/LivestreamProductMapperInterface';

export default class LivestreamProductMapper implements LivestreamProductMapperInterface {
    mapFromApiToEntity(data: unknown): LivestreamProductEntity | null {
        if (!data || Array.isArray(data) || typeof data !== 'object') {
            return null;
        }

        const raw = data as Record<string, unknown>;
        const id = NumberHelper.parseIntegerDefaultZero(raw.id);
        const name = StringHelper.trim(raw.name);
        const livestreamPrice = NumberHelper.parseIntegerDefaultZero(raw.livestreamPrice);

        if (NumberHelper.isPositive(id) === false || StringHelper.isHasValue(name) === false) {
            return null;
        }

        if (NumberHelper.isPositive(livestreamPrice) === false) {
            return null;
        }

        const result = new LivestreamProductEntity();
        result.id = id;
        result.name = name;
        result.livestreamPrice = livestreamPrice;
        result.livestreamPriceText = PriceFacade.getInstance().format(livestreamPrice) ?? '';

        const images = ArrayHelper.toArray(raw.images)
            .map((item) => StringHelper.trim(item))
            .filter((item) => StringHelper.isHasValue(item));
        result.images = images;

        result.sizes = ArrayHelper.toArray(raw.sizes)
            .map((item) => StringHelper.trim(item))
            .filter((item) => StringHelper.isHasValue(item));

        result.variations = this.mapVariations(raw.variations);
        if (ArrayHelper.isHasItems(result.variations)) {
            result.isHasStock = result.variations.some((item) => NumberHelper.isPositive(item.stock));
            if (ArrayHelper.isHasItems(result.sizes) === false) {
                result.sizes = result.variations
                    .map((item) => item.size)
                    .filter((item) => StringHelper.isHasValue(item));
            }
        } else if (ArrayHelper.isHasItems(result.sizes)) {
            result.isHasStock = true;
        } else {
            // BE simple product: sizes=[] + variation size="" → vẫn bán được nếu stock > 0
            const fallbackStock = this.extractFallbackStock(raw.variations);
            if (NumberHelper.isPositive(fallbackStock.stock) && NumberHelper.isPositive(fallbackStock.id)) {
                const fallbackSize = 'Free size';
                const variation = new LivestreamProductVariationEntity();
                variation.id = fallbackStock.id;
                variation.size = fallbackSize;
                variation.stock = fallbackStock.stock;
                result.variations = [variation];
                result.sizes = [fallbackSize];
                result.isHasStock = true;
            } else {
                result.isHasStock = false;
            }
        }

        result.sizes = sortBySize(result.sizes, (item) => item);
        result.variations = sortBySize(result.variations, (item) => item.size);

        return result;
    }

    /** API affiliate trả giá (đã giảm affiliate) ở `price` → gán vào `livestreamPrice` để UI/tính tiền dùng chung; `originalPrice` = giá gốc để gạch. */
    mapFromAffiliateApiToEntity(data: unknown): LivestreamProductEntity | null {
        if (!data || Array.isArray(data) || typeof data !== 'object') {
            return null;
        }

        const raw = data as Record<string, unknown>;
        const result = this.mapFromApiToEntity({ ...raw, livestreamPrice: raw.price });
        if (result) {
            const originalPrice = NumberHelper.parseIntegerDefaultZero(raw.originalPrice);
            result.originalPrice = originalPrice > result.livestreamPrice ? originalPrice : 0;
        }
        return result;
    }

    private extractFallbackStock(data: unknown): { id: number; stock: number } {
        for (const item of ArrayHelper.toArray(data)) {
            if (!item || typeof item !== 'object') {
                continue;
            }

            const raw = item as Record<string, unknown>;
            const id = NumberHelper.parseIntegerDefaultZero(raw.id);
            const stock = NumberHelper.parseIntegerDefaultZero(raw.stock);
            if (NumberHelper.isPositive(id) && NumberHelper.isPositiveIncludeZero(stock)) {
                return { id, stock };
            }
        }

        return { id: 0, stock: 0 };
    }

    private mapVariations(data: unknown): LivestreamProductVariationEntity[] {
        const list: LivestreamProductVariationEntity[] = [];
        for (const item of ArrayHelper.toArray(data)) {
            if (!item || typeof item !== 'object') {
                continue;
            }

            const raw = item as Record<string, unknown>;
            const id = NumberHelper.parseIntegerDefaultZero(raw.id);
            const size = StringHelper.trim(raw.size);
            const stock = NumberHelper.parseIntegerDefaultZero(raw.stock);

            if (NumberHelper.isPositive(id) === false || StringHelper.isHasValue(size) === false) {
                continue;
            }

            if (NumberHelper.isPositiveIncludeZero(stock) === false) {
                continue;
            }

            const variation = new LivestreamProductVariationEntity();
            variation.id = id;
            variation.size = size;
            variation.stock = stock;
            list.push(variation);
        }

        return list;
    }
}
